// Top Level (default) dashboard — also the fallback for any department without its own
// component yet. Mirrors the Finance dashboard's layout (Sales/Purchase/Approvals row,
// Outstanding + GST position + Recent Activity on the left, Quick access as a sidebar) using the
// same shared building blocks, plus the module-access/click-count line unique to this dashboard.
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TrendingUp, ShoppingCart, DollarSign, CreditCard, Wallet, Receipt, Scale } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import RecentTransactionsCard from './RecentTransactionsCard';
import ApprovalsCard from './ApprovalsCard';
import SummaryCard from './SummaryCard';
import MonthComparisonCard, { MONTH_COMPARISON_TONES } from './MonthComparisonCard';
import GSTPositionCard from './GSTPositionCard';
import OverheadPercentCard from './OverheadPercentCard';
import { useSalesPurchaseMetrics } from './useSalesPurchaseMetrics';
import {
    fetchClientOutStandingSummary,
    fetchVendorOutStandingSummary,
    fetchStockPurchaseConsolidateSummary,
    fetchFYIncomeExpenseSummary,
    fetchOverheadPercentSummary,
    selectReceivablesSummary,
    selectPayablesSummary,
    selectTaxLiabilitiesSummary,
    selectIncomeExpenseSummary,
    selectReceivablesLoading,
    selectPayablesLoading,
    selectTaxLiabilitiesLoading,
    selectIncomeExpenseLoading,
    selectOverheadSummary,
    selectOverheadLoading,
} from '../../slices/dashboardSlice/financialOverviewSlice';

// % change vs the previous FY; divides by |previous| so a margin moving from a loss towards a
// profit still reads as an increase.
const fyGrowth = (current, previous) => (previous
    ? parseFloat(((current - previous) / Math.abs(previous) * 100).toFixed(1))
    : 0);

const DashboardContent = ({ onNavigate, linkFrequency = {}, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const receivablesSummary = useSelector(selectReceivablesSummary);
    const payablesSummary = useSelector(selectPayablesSummary);
    const taxLiabilitiesSummary = useSelector(selectTaxLiabilitiesSummary);
    const receivablesLoading = useSelector(selectReceivablesLoading);
    const payablesLoading = useSelector(selectPayablesLoading);
    const taxLiabilitiesLoading = useSelector(selectTaxLiabilitiesLoading);
    const incomeExpense = useSelector(selectIncomeExpenseSummary);
    const incomeExpenseLoading = useSelector(selectIncomeExpenseLoading);
    const overheadSummary = useSelector(selectOverheadSummary);
    const overheadLoading = useSelector(selectOverheadLoading);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const groupId = userData?.groupId || userData?.GroupId || 0;
    const ccCodes = userData?.ccCodes || '';

    const {
        currentMonthSales, currentMonthPurchase,
        previousMonthSales, previousMonthPurchase,
        salesGrowth, purchaseGrowth,
    } = useSalesPurchaseMetrics(roleId, userId);

    useEffect(() => {
        dispatch(fetchClientOutStandingSummary({ roleId, userId }));
        dispatch(fetchVendorOutStandingSummary({ roleId, userId }));
        dispatch(fetchStockPurchaseConsolidateSummary({ roleId, userId }));
    }, [dispatch, roleId, userId]);

    // Company-wide, so it doesn't depend on the role/user.
    useEffect(() => {
        dispatch(fetchFYIncomeExpenseSummary());
        dispatch(fetchOverheadPercentSummary());
    }, [dispatch]);

    const fyCards = [
        { title: 'Total Income', icon: Wallet, tone: MONTH_COMPARISON_TONES.income, unfavorable: false,
          current: incomeExpense?.TotalIncomeCurrentFY, previous: incomeExpense?.TotalIncomePreviousFY },
        { title: 'Total Expenses', icon: Receipt, tone: MONTH_COMPARISON_TONES.expense, unfavorable: true,
          current: incomeExpense?.TotalExpensesCurrentFY, previous: incomeExpense?.TotalExpensesPreviousFY },
        { title: 'Provisional Margin', icon: Scale, tone: MONTH_COMPARISON_TONES.margin, unfavorable: false,
          current: incomeExpense?.ProvisionalMarginCurrentFY, previous: incomeExpense?.ProvisionalMarginPreviousFY },
    ];

    const totalClicks = Object.values(linkFrequency).reduce((sum, count) => sum + count, 0);
    const uniqueItemsClicked = Object.keys(linkFrequency).length;

    return (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                        <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode} </p>
                        <p className="text-orange-200 dark:text-orange-300 text-sm">
                            Access to {roleData?.menuItems?.length || 0} system modules
                            {totalClicks > 0 && (
                                <span className="ml-2 inline-flex items-center">
                                    • {totalClicks} clicks on {uniqueItemsClicked} items
                                </span>
                            )}
                        </p>
                    </div>
                    <div className="flex items-center space-x-4">
                        <div className="text-right text-orange-200 dark:text-orange-300">
                            <p className="text-sm">This Month Performance</p>
                            <p className="text-xl font-bold">
                                {salesGrowth > 0 ? '📈' : '📉'} {Math.abs(salesGrowth)}%
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sales / Purchase / Approvals + Outstanding & GST on the left, Quick access as a sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
                <div className="lg:col-span-3 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
                        <MonthComparisonCard
                            title="Sales this month"
                            icon={TrendingUp}
                            tone={MONTH_COMPARISON_TONES.sales}
                            amount={currentMonthSales}
                            growth={salesGrowth}
                            unfavorable={false}
                            currentValue={currentMonthSales}
                            previousValue={previousMonthSales}
                        />
                        <MonthComparisonCard
                            title="Purchase this month"
                            icon={ShoppingCart}
                            tone={MONTH_COMPARISON_TONES.purchase}
                            amount={currentMonthPurchase}
                            growth={purchaseGrowth}
                            unfavorable
                            currentValue={currentMonthPurchase}
                            previousValue={previousMonthPurchase}
                        />
                        <ApprovalsCard roleId={roleId} userId={userId} ccCodes={ccCodes} groupId={groupId} />
                    </div>

                    {/* Financial year — Total Income / Total Expenses / Provisional Margin, current FY vs previous FY */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                            Financial Year{incomeExpense?.CurrentFY ? ` ${incomeExpense.CurrentFY}` : ''}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-stretch">
                            {fyCards.map((c) => (
                                <MonthComparisonCard
                                    key={c.title}
                                    title={c.title}
                                    icon={c.icon}
                                    tone={c.tone}
                                    loading={incomeExpenseLoading}
                                    amount={c.current}
                                    growth={fyGrowth(c.current, c.previous)}
                                    unfavorable={c.unfavorable}
                                    currentValue={c.current}
                                    previousValue={c.previous}
                                    currentLabel={incomeExpense?.CurrentFY || 'This FY'}
                                    previousLabel={incomeExpense?.PreviousFY || 'Last FY'}
                                    changeLabel="last FY"
                                />
                            ))}
                        </div>
                    </div>

                    {/* Outstanding — Receivables & Payables (each with an age-bucket proportion bar) + GST position */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Outstanding</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                            <SummaryCard
                                title="Receivables"
                                subtitle="Client outstanding by age"
                                icon={DollarSign}
                                tone="green"
                                loading={receivablesLoading}
                                data={receivablesSummary}
                                total={receivablesSummary?.TotalBalance}
                                showBar
                                rows={[
                                    { label: '0 – 90 days', value: receivablesSummary?.InvoiceBalBelow, dot: 'bg-green-500' },
                                    { label: '91 – 120 days', value: receivablesSummary?.InvoiceBalBetween91to120, dot: 'bg-yellow-500' },
                                    { label: '121 – 180 days', value: receivablesSummary?.InvoiceBalBetween121to180, dot: 'bg-orange-500' },
                                    { label: 'Above 180 days', value: receivablesSummary?.InvoiceBalAbove, dot: 'bg-red-500' },
                                    { divider: true },
                                    { label: 'Retention', value: receivablesSummary?.RetentionBalance, dot: 'bg-blue-400' },
                                    { label: 'Hold', value: receivablesSummary?.HoldBalance, dot: 'bg-gray-400' },
                                ]}
                            />

                            <SummaryCard
                                title="Payables"
                                subtitle="Vendor outstanding by age"
                                icon={CreditCard}
                                tone="red"
                                loading={payablesLoading}
                                data={payablesSummary}
                                total={payablesSummary?.TotalBalance}
                                showBar
                                rows={[
                                    { label: '0 – 30 days', value: payablesSummary?.Below30Days, dot: 'bg-green-500' },
                                    { label: '31 – 60 days', value: payablesSummary?.Between31And60Days, dot: 'bg-lime-500' },
                                    { label: '61 – 90 days', value: payablesSummary?.Between61And90Days, dot: 'bg-yellow-500' },
                                    { label: '91 – 120 days', value: payablesSummary?.Between91And120Days, dot: 'bg-orange-500' },
                                    { label: 'Above 120 days', value: payablesSummary?.Above120Days, dot: 'bg-red-500' },
                                    { divider: true },
                                    { label: 'Retention', value: payablesSummary?.RetentionBalance, dot: 'bg-blue-400' },
                                    { label: 'Hold', value: payablesSummary?.HoldBalance, dot: 'bg-gray-400' },
                                ]}
                            />

                            <GSTPositionCard
                                loading={taxLiabilitiesLoading}
                                summary={taxLiabilitiesSummary}
                            />
                        </div>
                    </div>

                    {/* Today's Recent Activity — kept alongside Outstanding so its width matches */}
                    <RecentTransactionsCard roleId={roleId} userId={userId} />
                </div>

                <div className="lg:col-span-1 space-y-4">
                    <OverheadPercentCard loading={overheadLoading} summary={overheadSummary} />
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                        extraSection={{
                            title: 'Reports & Statements',
                            keywords: ['ledger', 'transaction log', 'account status', 'overall status', 'gst'],
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DashboardContent;
